import React from 'react'
import {menu, footerLinks, socialItems} from './data'
import FooterSocial from './FooterSocial'
import FooterLinks from './FooterLinks'
import FooterIcons from './FooterIcons'

import styled, {css, ThemeProvider} from 'styled-components'
import {Box, Link, LanguageSwitcher, useWindowSize} from '@miro-site/core'
import theme from './theme'
import {AccordionMenu} from '@miro-site/components_header'

import type {ICategory, IFooterItem} from './api/interfaces'

import {usePageTranslations} from '@miro-site/localization'
import {useLocalization} from '@miro-site/localization'
interface FooterProps {
  label: string
  categories: ICategory[]
  socialMedia: IFooterItem[]
  bottomLinks: IFooterItem[]
  className?: string
}

const FooterComponent = styled(Box).attrs({
  as: 'footer',
  $styles: {
    background: theme.footer.background,
    fontFamily: 'Formular',
    fontFeatureSettings: "'ss03'",
  },
})`
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }
`

const FooterContainer = styled(Box).attrs({
  $styles: {
    maxWidth: theme.footer.container.width,
    margin: theme.footer.container.margin,
    paddingTop: theme.footer.container.padding.top,
    paddingLeft: theme.footer.container.padding.left,
    paddingRight: theme.footer.container.padding.right,
    paddingBottom: theme.footer.container.padding.bottom,
  },
})``

const FooterList = styled(Box).attrs({
  as: 'ul',
  $styles: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    marginLeft: theme.footer.list.margin,
    marginRight: theme.footer.list.margin,
    paddingRight: theme.footer.list.padding.right,
  },
})``

const FooterListItem = styled(Box).attrs({
  as: 'li',
  $styles: {
    flex: '0 0 20%',
    paddingLeft: theme.footer.item.padding,
    paddingRight: theme.footer.item.padding,
  },
})``

const FooterListTitleWrapper = styled(Box).attrs({
  $styles: {
    marginBottom: theme.footer.categoryWrapper.margin.bottom,
  },
})``

const FooterListTitle = styled(Box).attrs({
  as: 'h3',
  $styles: {
    fontSize: theme.footer.categoryTitle.fontSize,
    lineHeight: theme.footer.categoryTitle.lineHeight,
    fontWeight: theme.footer.categoryTitle.fontWeight,
  },
})`
  ${({theme}) => css`
    color: ${theme.colors.primaryWhite};
  `}
`

const FooterInnerWrapper = styled(Box).attrs<{$isActive?: boolean}>(({$isActive}) => ({
  $styles: {
    overflow: ['hidden', null],
  },
}))<{$isActive?: boolean}>``

const FooterInnerItem = styled(Box).attrs({
  as: 'li',
  $mt: theme.footer.innerItem.margin.top,
})`
  &:first-child {
    margin-top: 0;
  }
`

const FooterInnerLink = styled(Link)`
  ${props => props.theme.normalizeStyles(theme.footer.links.menu)}
`

const FooterAdditionalLink = styled(Link).attrs({})`
  ${props => props.theme.normalizeStyles(theme.footer.links.additional)}
`

const FooterBottomContainer = styled(Box).attrs({
  $styles: {
    borderTop: '1px solid #9B99AF',
  },
  $mt: theme.footer.bottomContainer.margin.top,
  $pt: theme.footer.bottomContainer.padding.top,
})``

const FooterRow = styled(Box).attrs({
  as: 'div',
  $styles: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    textAlign: ['center', 'center'],
  },
})``

const FooterCol = styled(Box).attrs({
  $styles: {
    flex: ['0 0 100%', '0 0 100%', '0 0 auto', '0 0 auto', '0 0 auto'],
  },
})`
  &:nth-of-type(1) {
    ${({theme}) =>
      theme.normalizeStyles({
        order: [2, 2, 1, 1],
      })}
  }
  &:nth-of-type(2) {
    ${({theme}) =>
      theme.normalizeStyles({
        order: [1, 1, 2, 2],
      })}
  }
`

const FooterInnerRow = styled(Box).attrs({
  $styles: {
    display: 'flex',
    justifyContent: ['center', 'center', '', '', ''],
    alignItems: 'center',
    flexWrap: 'wrap',
  },
})``

const FooterInnerCol = styled(Box).attrs({
  $styles: {
    flex: ['0 0 100%', '0 0 100%', '0 0 auto'],
  },
  $mr: theme.footer.innerColumn.margin.right,
})`
  &:last-child {
    margin-right: 0;
  }
`

const FooterInnerColWrapper = styled(Box).attrs({
  $styles: {
    display: 'flex',
    flexWrap: ['wrap', 'wrap', undefined],
    justifyContent: ['center', 'center', undefined],
  },
})``

const LanguageSwitcherContainer = styled(Box).attrs({
  $mr: [null, null, 20, 24],
})``

const Footer: React.FC<FooterProps> = props => {
  const {label, categories = menu, socialMedia = socialItems, bottomLinks = footerLinks} = props
  const [switcherData, {onChangeLocale}] = usePageTranslations()
  const {width} = useWindowSize()
  const {currentLocale} = useLocalization()
  const isRUlocale = currentLocale == 'ru' ? true : false
  const isMobileScreen = width <= theme.breakpoints.v2Tablet

  return (
    <ThemeProvider theme={defaultTheme => ({...defaultTheme, ...theme})}>
      <FooterComponent data-place="footer">
        <FooterContainer>
          {!isMobileScreen && (
            <nav aria-label={label} data-track-place="Footer">
              <FooterList>
                {categories.map((category, categoryIndex) => {
                  return (
                    <FooterListItem key={categoryIndex}>
                      <FooterListTitleWrapper>
                        <FooterListTitle>{category.title}</FooterListTitle>
                      </FooterListTitleWrapper>

                      <FooterInnerWrapper>
                        <ul>
                          {category.groups.map((subcategory, subcategoryIndex) => {
                            return (
                              <FooterInnerItem key={subcategoryIndex}>
                                <ul>
                                  {subcategory.items.map((link, linkIndex) => {
                                    return (
                                      <FooterInnerItem key={linkIndex}>
                                        <FooterInnerLink
                                          href={link.path}
                                          $color="white"
                                          $size="medium"
                                        >
                                          {link.title}
                                        </FooterInnerLink>
                                      </FooterInnerItem>
                                    )
                                  })}
                                </ul>

                                {subcategory.viewAllLink && !isRUlocale && (
                                  <FooterAdditionalLink
                                    href={subcategory.viewAllLink.path}
                                    $variant="regular"
                                    $color="white"
                                    $size="medium"
                                    withArrow={true}
                                  >
                                    {subcategory.viewAllLink.title}
                                  </FooterAdditionalLink>
                                )}
                              </FooterInnerItem>
                            )
                          })}
                        </ul>
                      </FooterInnerWrapper>
                    </FooterListItem>
                  )
                })}
              </FooterList>
            </nav>
          )}

          {isMobileScreen && <AccordionMenu categories={categories} />}

          <FooterBottomContainer>
            <FooterRow>
              <FooterCol>
                <FooterInnerRow>
                  <FooterInnerCol>
                    <FooterSocial items={socialMedia} />
                  </FooterInnerCol>

                  <FooterInnerCol>
                    <FooterInnerColWrapper>
                      <LanguageSwitcherContainer>
                        {switcherData && (
                          <LanguageSwitcher
                            {...switcherData}
                            onSelect={(e, link) => {
                              onChangeLocale(link.locale)
                            }}
                            color="white"
                            position="top"
                          />
                        )}
                      </LanguageSwitcherContainer>

                      <FooterLinks links={bottomLinks} />
                    </FooterInnerColWrapper>
                  </FooterInnerCol>
                </FooterInnerRow>
              </FooterCol>

              <FooterCol>
                <FooterIcons />
              </FooterCol>
            </FooterRow>
          </FooterBottomContainer>
        </FooterContainer>
      </FooterComponent>
    </ThemeProvider>
  )
}

export default Footer
